import { Badge, IconButton, IconKeyboardShortcutsLine, AccessibleContent } from '@smarterservices/smarter-ui';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * SessionChatIndicator Component
 * 
 * Displays a badge with the number of unread chat messages for a session.
 * The badge only appears when there are unread messages.
 * Clicking the badge opens the session in focus mode with the chat tab selected.
 * 
 * @component
 * @param {Object} props
 * @param {string} props.sessionSid - Session identifier to get unread count for
 * 
 * @example
 * <SessionChatIndicator sessionSid="session_123" />
 */
function SessionChatIndicator({ sessionSid }) {
    // Subscribe to the separate unreadMessageCounts map (not the sessions object)
    // to avoid re-renders when other session properties change
    const unreadMessageCount = useSessionStore(
        (state) => state.unreadMessageCounts?.[sessionSid] || 0
    );

    // Get the openDrawer action from app store
    const openDrawer = useAppStore((state) => state.openDrawer);

    // Don't render anything if there are no unread messages
    if (!unreadMessageCount || unreadMessageCount === 0) {
        return null;
    }

    const handleClick = () => {
        // Open focus drawer with communication tab pre-selected
        openDrawer('focus', sessionSid, { initialTab: 'communication' });
    };

    return (

        <Badge
            key={unreadMessageCount} // Force remount when count changes to restart pulse animation
            count={unreadMessageCount}
            pulse
            variant="danger"
            interaction="readonly"
            themeOverride={{
                size: '1rem',
                fontSize: '0.6rem',
                countOffset: '0rem',
                countPadding: '0.1rem'
            }}

            formatOutput={function (formattedCount) {
                return (
                    <AccessibleContent alt={`You have ${formattedCount} new edits to review`}>
                        {formattedCount}
                    </AccessibleContent>
                )
            }}
        >
            <IconButton
                renderIcon={IconKeyboardShortcutsLine}
                screenReaderLabel="View unread messages"
                withBorder={false}
                withBackground={false}
                onClick={handleClick}
            />
        </Badge>
    );
}

export default SessionChatIndicator;
